import { DndContext, DragOverlay, MouseSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { beispielDaten, blaetterSlots, spalten, wendeAlleAn, type Daten, type Form, type Operation, type UiVoll } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { Undo2 } from "lucide-react";
import { useParams, useSearchParams } from "react-router";
import { schluessel, useKatalog, useNotizen, useStruktur, useUi } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { personFarbe, useAnwesende } from "../live/Anwesenheit.tsx";
import { aufFremdeAenderung, einreihen, uiGeladen, type FremdeAenderung } from "../live/live.ts";
import { Eigenschaften } from "../eigenschaften/Eigenschaften.tsx";
import { Blatt } from "../huelle/Blatt.tsx";
import { useDarf, useKonto } from "../huelle/konto.ts";
import { findePfad, findeUi } from "../huelle/pfad.ts";
import { ItemBild } from "../inventar/ItemBild.tsx";
import { useBefunde } from "../planung/befunde.ts";
import { Notizen } from "../planung/Notizen.tsx";
import { Pruefliste } from "../planung/Pruefliste.tsx";
import { Verlauf } from "../planung/Verlauf.tsx";
import { useHinweis } from "../ui/Hinweise.tsx";
import { klick, LEER, rahmen, strgKlick, umschaltKlick, type Auswahl } from "./auswahl.ts";
import { Rueckgaengig } from "./rueckgaengig.ts";
import { pfeil, PFEILE } from "./tastatur.ts";
import { verschiebeOps } from "./verschiebung.ts";
import { einfuegeOps, kopiere, lies, type Ablage } from "./zwischenablage.ts";
import { Zeichenflaeche } from "./Zeichenflaeche.tsx";
import "./editor.css";

let ablageSpeicher: Ablage | null = null;

function SlotZiel({ index, ziehbar, children, onBandStart, onBandUeber }: { index: number; ziehbar: boolean; children: ReactNode; onBandStart: (i: number, e: PointerEvent) => void; onBandUeber: (i: number, e: PointerEvent) => void }) {
  const zieh = useDraggable({ id: `s${index}`, disabled: !ziehbar });
  const ablage = useDroppable({ id: `z${index}` });
  return (
    <div
      ref={(el) => {
        zieh.setNodeRef(el);
        ablage.setNodeRef(el);
      }}
      {...(ziehbar ? zieh.listeners : {})}
      className={`slot-huelle${ablage.isOver ? " slot-drueber" : ""}${zieh.isDragging ? " slot-gezogen" : ""}`}
      onPointerDown={(e) => {
        if (!ziehbar) onBandStart(index, e);
      }}
      onPointerEnter={(e) => onBandUeber(index, e)}
    >
      {children}
    </div>
  );
}

export function EditorSeite() {
  const p = useParams();
  const struktur = useStruktur();
  const pfad = struktur.data ? findePfad(struktur.data, { plugin: p.plugin, workspace: p.workspace, ui: p.ui }) : null;
  const id = pfad?.ui?.id;
  const ui = useUi(id);
  const katalog = useKatalog(pfad?.plugin?.id);
  const darf = useDarf("bearbeiten");
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const [zustand, setZustand] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<Auswahl>(LEER);
  const [fokus, setFokus] = useState<number | null>(null);
  const [gezogen, setGezogen] = useState<number | null>(null);
  const [verschiebeVon, setVerschiebeVon] = useState<number | null>(null);
  const [blattOffen, setBlattOffen] = useState(false);
  const rueck = useRef(new Rueckgaengig());
  const band = useRef<number | null>(null);
  const kopierTaste = useRef(false);
  const flaeche = useRef<HTMLDivElement>(null);
  const sensoren = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));
  const [suche, setSuche] = useSearchParams();
  const notizListe = useNotizen("ui", id);
  const befunde = useBefunde(ui.data);
  const notizSlots = useMemo(() => new Set((notizListe.data ?? []).flatMap((n) => (n.ziel.slot !== undefined && !n.erledigt ? [n.ziel.slot] : []))), [notizListe.data]);

  const springen = useCallback((i: number) => {
    setAuswahl(klick(LEER, i));
    setFokus(i);
    window.requestAnimationFrame(() => flaeche.current?.querySelector<HTMLButtonElement>(`[data-slot="${i}"]`)?.focus());
  }, []);

  useEffect(() => {
    setAuswahl(LEER);
    setFokus(null);
    setZustand(null);
    setVerschiebeVon(null);
    setBlattOffen(false);
    rueck.current.leeren();
  }, [id]);

  const verweise = useCallback((uiId: string) => (struktur.data ? findeUi(struktur.data, uiId)?.ui.schluessel : undefined), [struktur.data]);

  const quellen: Daten = useMemo(() => {
    if (!ui.data || !katalog.data) return {};
    const d = structuredClone(beispielDaten(ui.data.daten, katalog.data));
    const q = ui.data.daten.quelle;
    if (q && d[q]) {
      if (zustand) d[q].zustand = zustand;
      else delete d[q].zustand;
    }
    return d;
  }, [ui.data, katalog.data, zustand]);

  const aendern = useCallback(
    (ops: Operation[], merken = true) => {
      if (!id || ops.length === 0) return;
      const key = schluessel.ui(id);
      const aktuell = qc.getQueryData<UiVoll>(key);
      if (!aktuell) return;
      let ergebnis: ReturnType<typeof wendeAlleAn>;
      try {
        ergebnis = wendeAlleAn(aktuell.daten, ops);
      } catch (e) {
        hinweis({ text: (e as Error).message, art: "fehler" });
        return;
      }
      qc.setQueryData<UiVoll>(key, { ...aktuell, daten: ergebnis.ui });
      if (merken) rueck.current.merken(ergebnis.umkehr, ops);
      einreihen(id, ops);
    },
    [id, qc, hinweis],
  );

  const zurueck = useCallback(() => {
    const ops = rueck.current.rueckgaengig();
    if (ops) aendern(ops, false);
  }, [aendern]);
  const vor = useCallback(() => {
    const ops = rueck.current.wiederholen();
    if (ops) aendern(ops, false);
  }, [aendern]);

  useEffect(() => {
    const taste = (e: globalThis.KeyboardEvent) => {
      kopierTaste.current = e.ctrlKey || e.altKey || e.metaKey;
      const tippt = (e.target as HTMLElement).closest?.("input, textarea, select, [contenteditable='true']");
      if (tippt || !(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        zurueck();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        vor();
      }
    };
    const los = (e: globalThis.KeyboardEvent) => {
      kopierTaste.current = e.ctrlKey || e.altKey || e.metaKey;
    };
    const hoch = () => {
      band.current = null;
    };
    window.addEventListener("keydown", taste);
    window.addEventListener("keyup", los);
    window.addEventListener("pointerup", hoch);
    return () => {
      window.removeEventListener("keydown", taste);
      window.removeEventListener("keyup", los);
      window.removeEventListener("pointerup", hoch);
    };
  }, [zurueck, vor]);

  const konto = useKonto();
  const anwesende = useAnwesende(id);
  const [fremd, setFremd] = useState<FremdeAenderung | null>(null);

  useEffect(() => {
    if (id && ui.data) uiGeladen(id, ui.data.revision);
  }, [id, ui.dataUpdatedAt]);

  useEffect(() => {
    if (!id) return;
    const melden = () => void api("/api/anwesenheit", { methode: "POST", body: { ui: id, slot: fokus } }).catch(() => {});
    const verzoegert = window.setTimeout(melden, 300);
    const takt = window.setInterval(melden, 15_000);
    return () => {
      window.clearTimeout(verzoegert);
      window.clearInterval(takt);
    };
  }, [id, fokus]);

  useEffect(
    () =>
      aufFremdeAenderung((f) => {
        if (f.ui !== id) return;
        setFremd(f);
        window.setTimeout(() => setFremd((x) => (x === f ? null : x)), 4000);
      }),
    [id],
  );

  useEffect(() => {
    const s = suche.get("slot");
    if (s === null || !ui.data) return;
    springen(Number(s));
    const neu = new URLSearchParams(suche);
    neu.delete("slot");
    setSuche(neu, { replace: true });
  }, [id, !!ui.data]);

  useEffect(() => {
    if (fokus === null) return;
    const el = flaeche.current?.querySelector<HTMLButtonElement>(`[data-slot="${fokus}"]`);
    if (el && flaeche.current?.contains(document.activeElement)) el.focus();
  }, [fokus]);

  if (struktur.data && !pfad?.ui) {
    return (
      <main className="seite">
        <h1>Diese UI gibt es nicht.</h1>
        <p className="leise">Vielleicht wurde sie umbenannt, verschoben oder gelöscht.</p>
      </main>
    );
  }
  if (!ui.data || !katalog.data) return <main className="editor" aria-busy="true" />;

  const daten = ui.data.daten;
  const sp = spalten(daten.form);
  const fest = (i: number) => !!daten.slots[i];
  const zustaende = katalog.data.quellen.find((q) => q.schluessel === daten.quelle)?.menueZustaende ?? [];
  const gewaehlt = new Set(auswahl.slots);

  const slotKlick = (i: number, e: MouseEvent | KeyboardEvent) => {
    if (verschiebeVon !== null) {
      const von = verschiebeVon;
      setVerschiebeVon(null);
      if (i === von) return;
      const op = verschiebeOps([von], von, i, daten.form, false);
      if (!op) return hinweis({ text: "Dorthin lässt sich der Slot nicht verschieben.", art: "fehler" });
      aendern([op]);
      setAuswahl(klick(LEER, i));
      setFokus(i);
      setBlattOffen(true);
      return;
    }
    setBlattOffen(true);
    setFokus(i);
    if (e.shiftKey) setAuswahl((a) => umschaltKlick(a, i, sp));
    else if (e.ctrlKey || e.metaKey) setAuswahl((a) => strgKlick(a, i));
    else setAuswahl((a) => klick(a, i));
  };

  const kopieren = async () => {
    const a = kopiere(daten, auswahl.slots);
    if (!a) return;
    ablageSpeicher = a;
    try {
      await navigator.clipboard.writeText(JSON.stringify(a));
    } catch {
      // Ohne Zugriff auf die Zwischenablage bleibt der interne Speicher.
    }
    hinweis({ text: `${a.slots.length} ${a.slots.length === 1 ? "Slot" : "Slots"} kopiert.` });
  };

  const einfuegen = async () => {
    let a = ablageSpeicher;
    try {
      a = lies(await navigator.clipboard.readText()) ?? a;
    } catch {
      // Interner Speicher reicht.
    }
    const ziel = fokus ?? auswahl.slots[0];
    if (!a || ziel === undefined) return;
    aendern(einfuegeOps(a, ziel, daten));
  };

  const inventarTaste = (e: KeyboardEvent) => {
    const strg = e.ctrlKey || e.metaKey;
    const richtung = PFEILE[e.key];
    if (richtung) {
      e.preventDefault();
      const von = fokus ?? 0;
      const neu = pfeil(von, richtung, daten.form);
      setFokus(neu);
      if (e.shiftKey) setAuswahl((a) => ({ slots: rahmen(a.anker ?? von, neu, sp), anker: a.anker ?? von, bereich: null }));
      else if (!strg) setAuswahl((a) => klick(a, neu));
      return;
    }
    if ((e.key === "Delete" || e.key === "Backspace") && darf) {
      e.preventDefault();
      const belegt = auswahl.slots.filter(fest);
      if (belegt.length) aendern([{ art: "slots_leeren", indizes: belegt }]);
    } else if (e.key === "Escape") {
      if (verschiebeVon !== null) return setVerschiebeVon(null);
      setAuswahl(LEER);
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      document.querySelector<HTMLElement>(".eigenschaften input, .eigenschaften textarea, .eigenschaften .itemwahl")?.focus();
    } else if (strg && e.key.toLowerCase() === "c") {
      e.preventDefault();
      void kopieren();
    } else if (strg && e.key.toLowerCase() === "v" && darf) {
      e.preventDefault();
      void einfuegen();
    }
  };

  const ziehEnde = (e: DragEndEvent) => {
    const von = gezogen;
    setGezogen(null);
    if (von === null || !e.over) return;
    const ziel = Number(String(e.over.id).slice(1));
    const menge = gewaehlt.has(von) ? auswahl.slots.filter(fest) : [von];
    const op = verschiebeOps(menge, von, ziel, daten.form, kopierTaste.current);
    if (!op) {
      if (ziel !== von) hinweis({ text: "So passt die Auswahl nicht in die Form.", art: "fehler" });
      return;
    }
    aendern([op]);
    if (op.art === "slots_verschieben") {
      setAuswahl({ slots: op.paare.map((x) => x.nach), anker: ziel, bereich: null });
      setFokus(ziel);
    }
  };

  const blaetter = new Set(blaetterSlots(daten));
  const fremde = (anwesende.data ?? [])
    .filter((x) => x.kontoId !== konto.kontoId && x.slot !== null)
    .map((x) => ({ slot: x.slot!, farbe: personFarbe(x.kontoId, x.istClaude), kuerzel: x.name.slice(0, 1).toUpperCase(), name: x.name }));
  const fremdHier = fremd && (fremd.ui_felder ? auswahl.slots.length === 0 : fremd.slots.some((s) => gewaehlt.has(s)));
  const fremdHinweis = fremdHier ? (
    <p className="fremd-hinweis" role="status">
      Gerade geändert von {fremd.autor}
    </p>
  ) : null;
  const verlaufAn = suche.get("ansicht") === "verlauf";
  const verlaufZu = () => {
    const neu = new URLSearchParams(suche);
    neu.delete("ansicht");
    setSuche(neu);
  };
  const blattZu = () => {
    setBlattOffen(false);
    if (verlaufAn) verlaufZu();
  };
  const verschieben = (von: number) => {
    setVerschiebeVon(von);
    setBlattOffen(false);
  };
  const blattTitel = verlaufAn
    ? "Verlauf"
    : auswahl.bereich
      ? `Listenbereich ${auswahl.bereich}`
      : auswahl.slots.length === 1
        ? `Slot ${auswahl.slots[0]}`
        : auswahl.slots.length > 1
          ? `${auswahl.slots.length} Slots`
          : (ui.data.name ?? ui.data.schluessel);
  const slotHuelle = (i: number, inhalt: ReactNode) => (
    <SlotZiel
      index={i}
      ziehbar={darf && fest(i)}
      onBandStart={(start, e) => {
        if (e.pointerType === "mouse" && e.button === 0 && !blaetter.has(start)) band.current = start;
      }}
      onBandUeber={(j, e) => {
        if (band.current !== null && e.buttons === 1 && j !== band.current) setAuswahl({ slots: rahmen(band.current, j, sp), anker: band.current, bereich: null });
      }}
    >
      {inhalt}
    </SlotZiel>
  );

  return (
    <main className="editor">
      <div
        ref={flaeche}
        className="editor-flaeche"
        onPointerDown={(e) => {
          if ((e.target as HTMLElement).classList.contains("buehne")) setAuswahl(LEER);
        }}
      >
        <DndContext sensors={sensoren} onDragStart={(e) => setGezogen(Number(String(e.active.id).slice(1)))} onDragEnd={ziehEnde} onDragCancel={() => setGezogen(null)}>
          <Zeichenflaeche
            daten={daten}
            katalog={katalog.data}
            quellen={quellen}
            zustaende={zustaende}
            zustand={zustand}
            onZustand={setZustand}
            verweise={verweise}
            darfBearbeiten={darf}
            onForm={(form: Form) => aendern([{ art: "ui_setzen", felder: { form } }])}
            auswahl={gewaehlt}
            fokus={fokus}
            fremde={fremde}
            notizen={notizSlots}
            onSlot={slotKlick}
            onTaste={inventarTaste}
            slotHuelle={slotHuelle}
            werkzeuge={
              <div className="werkzeug-rechts">
                <button type="button" className="knopf knopf-still" disabled={!rueck.current.kannZurueck()} onClick={zurueck} title="Rückgängig (Strg+Z)" aria-label="Rückgängig">
                  <Undo2 size={14} aria-hidden="true" />
                  <span className="werkzeug-knopf-text">Rückgängig</span>
                </button>
              </div>
            }
          />
          <DragOverlay dropAnimation={null}>
            {gezogen !== null && daten.slots[gezogen] && (
              <div className="zieh-bild">
                <ItemBild symbol={daten.slots[gezogen]!.symbol} groesse={36} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
        {verschiebeVon !== null && (
          <div className="verschiebe-hinweis" role="status">
            <span>Zielslot antippen, um Slot {verschiebeVon} zu verschieben.</span>
            <button type="button" className="knopf knopf-still" onClick={() => setVerschiebeVon(null)}>
              Abbrechen
            </button>
          </div>
        )}
      </div>
      <Blatt offen={blattOffen || verlaufAn} titel={blattTitel} onOffen={() => setBlattOffen(true)} onSchliessen={blattZu}>
        {verlaufAn ? (
          <Verlauf ui={ui.data} onSchliessen={verlaufZu} />
        ) : (
          <Eigenschaften
            k={{ ui: ui.data, katalog: katalog.data, aendern, darf, verschieben }}
            auswahl={auswahl}
            oben={fremdHinweis}
            unten={
              <>
                <Notizen key={id} ziel={{ art: "ui", id: ui.data.id }} slot={auswahl.slots.length === 1 && !auswahl.bereich ? auswahl.slots[0]! : null} onSlot={springen} />
                <Pruefliste befunde={befunde} onSlot={springen} />
              </>
            }
          />
        )}
      </Blatt>
    </main>
  );
}
